'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useDeviceType } from '@/hooks/useIsMobile';
import { OsType, useBoardEditorStore } from '@/stores/useBoardEditorStore';
import { useModalAlertStore } from '@/stores/useModalAlertStore';
import BoardSplitEditor from '@/(routers)/boards/editor/components/unit/BoardSplitEditor';

const MOBILE_FOOTER_HEIGHT = 70;
export interface BoardEditorProps {}

export default function BoardEditor({}: BoardEditorProps) {
    const holderRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState<number>(480);
    const { isMobile, isMobileDevice, isTabletDevice, isDesktopDevice, isPortrait } = useDeviceType();
    const { openModalAlert, closeModalAlert } = useModalAlertStore();
    const router = useRouter();
    const { setOsType } = useBoardEditorStore();

    useEffect(() => {
        const osType = detectOsKey();
        if (osType) {
            setOsType(osType);
        }
    }, [setOsType]);

    function detectOsKey(): OsType | null {
        if (typeof window === 'undefined') return null;
        const ua = window.navigator.userAgent.toLowerCase();
        if (ua.includes('cros')) return 'chromebook';
        if (ua.includes('windows')) return 'windows';
        if (ua.includes('macintosh') || ua.includes('mac os')) return 'mac';
        return null;
    }

    useEffect(() => {
        const compute = () => {
            if (!holderRef.current) return;
            const isLgDown = window.innerWidth <= 768;
            const top = holderRef.current.getBoundingClientRect().top + (isLgDown ? 0 : 8);
            const vh = window.visualViewport?.height ?? window.innerHeight;
            const footerOffset = isLgDown ? MOBILE_FOOTER_HEIGHT : 0;
            const next = Math.max(160, Math.floor(vh - top - footerOffset));
            setHeight(next);
        };
        compute();

        const ro = new ResizeObserver(compute);
        ro.observe(document.body);

        window.addEventListener('resize', compute);
        window.addEventListener('scroll', compute, { passive: true });
        window.visualViewport?.addEventListener?.('resize', compute);

        return () => {
            ro.disconnect();
            window.removeEventListener('resize', compute);
            window.removeEventListener('scroll', compute);
            window.visualViewport?.removeEventListener?.('resize', compute);
        };
    }, []);

    useEffect(() => {
        if (isMobileDevice) {
            closeModalAlert();
            router.push('/error/boards/mobile');
            return;
        }

        const isTabletPortrait = isTabletDevice && isPortrait;
        const isDesktopNarrow = isDesktopDevice && isMobile;

        if (isTabletPortrait || isDesktopNarrow) {
            openModalAlert({
                id: 'block-to-code',
                message: 'こちらの画面サイズには対応していません',
                subMessage: 'PCまたはタブレットを横向きにしてご利用ください。',
                variant: 'warning',
                onAlert: () => {},
            });
        } else {
            closeModalAlert();
        }
    }, [isMobileDevice, isTabletDevice, isDesktopDevice, isPortrait, isMobile, router]);

    if (isMobileDevice) {
        return null;
    }

    return (
        <div className="flex justify-center w-full" style={{ height }}>
            <div className="flex flex-col justify-start md:justify-center items-center md:items-start w-full h-full">
                <div ref={holderRef} style={{ height: '100%' }} className="w-full">
                    <div className="h-full">
                        <BoardSplitEditor />
                    </div>
                </div>
            </div>
        </div>
    );
}
